// In src/pages/LandingPage/Others/Usermanualpage.jsx
import React, { useEffect, useState } from "react";
import {
  BookOpen,
  LogIn,
  Home as HomeIcon,
  PieChart,
  PlusCircle,
  Search as SearchIcon,
  Settings as SettingsIcon,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import PageLayout from "../../../components/layout/PageLayout";
import PageHeader from "../../../components/layout/PageHeader";

const Usermanualpage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [open, setOpen] = useState({
    auth: true,
    home: false,
    nav: false,
    reports: false,
    add: false,
    search: false,
    profile: false,
  });

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const AccordionItem = ({ id, title, icon, children, delay = "" }) => (
    <div
      className={`bg-[var(--color-surface)] border border-[var(--color-border)] p-4 sm:p-6 rounded-xl transition-all duration-700 hover:border-[var(--color-text)]/30 ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
      } ${delay}`}
    >
      <button
        className="w-full flex items-center justify-between"
        onClick={() => setOpen((s) => ({ ...s, [id]: !s[id] }))}
        aria-expanded={open[id]}
      >
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-[var(--color-surface-hover)]">
            {icon}
          </div>
          <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-[var(--color-text)]">
            {title}
          </h2>
        </div>
        {open[id] ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </button>
      <div
        className={`grid transition-all overflow-hidden ${
          open[id] ? "grid-rows-[1fr] mt-4" : "grid-rows-[0fr]"
        }`}
      >
        <div className="min-h-0">
          {children}
        </div>
      </div>
    </div>
  );

  return (
    <PageLayout className="relative overflow-hidden text-[var(--color-text)]">
      <PageHeader title="User Manual" showBack={true} />

      <div className="max-w-full mx-auto text-center mt-6">
        {/* Background (subtle gradient) */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(255,255,255,0.05)_0%,rgba(0,0,0,0)_70%)] -z-10" />
        
        <div className="container-section relative z-10 w-full px-4 sm:px-6 lg:px-8">
              {/* Quick Start ribbon */}
              <div className={`grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 text-left ${
                isVisible ? "opacity-100" : "opacity-0"
              } transition-opacity duration-700`}>
                <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl p-4 flex items-center gap-3">
                  <LogIn size={18} className="text-[var(--color-text)] opacity-80" />
                  <div>
                    <div className="text-xs uppercase tracking-wide text-[var(--color-text)] opacity-60">Step 1</div>
                    <div className="text-sm font-medium text-[var(--color-text)]/90">Sign in</div>
                  </div>
                </div>
                <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl p-4 flex items-center gap-3">
                  <HomeIcon size={18} className="text-[var(--color-text)] opacity-80" />
                  <div>
                    <div className="text-xs uppercase tracking-wide text-[var(--color-text)] opacity-60">Step 2</div>
                    <div className="text-sm font-medium text-[var(--color-text)]/90">Explore Home</div>
                  </div>
                </div>
                <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl p-4 flex items-center gap-3">
                  <PlusCircle size={18} className="text-[var(--color-text)] opacity-80" />
                  <div>
                    <div className="text-xs uppercase tracking-wide text-[var(--color-text)] opacity-60">Step 3</div>
                    <div className="text-sm font-medium text-[var(--color-text)]/90">Add Expense</div>
                  </div>
                </div>
                <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl p-4 flex items-center gap-3">
                  <PieChart size={18} className="text-[var(--color-text)] opacity-80" />
                  <div>
                    <div className="text-xs uppercase tracking-wide text-[var(--color-text)] opacity-60">Step 4</div>
                    <div className="text-sm font-medium text-[var(--color-text)]/90">See Reports</div>
                  </div>
                </div>
                <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl p-4 flex items-center gap-3">
                  <SearchIcon size={18} className="text-[var(--color-text)] opacity-80" />
                  <div>
                    <div className="text-xs uppercase tracking-wide text-[var(--color-text)] opacity-60">Step 5</div>
                    <div className="text-sm font-medium text-[var(--color-text)]/90">Search</div>
                  </div>
                </div>
                <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl p-4 flex items-center gap-3">
                  <SettingsIcon size={18} className="text-[var(--color-text)] opacity-80" />
                  <div>
                    <div className="text-xs uppercase tracking-wide text-[var(--color-text)] opacity-60">Step 6</div>
                    <div className="text-sm font-medium text-[var(--color-text)]/90">Profile/Settings</div>
                  </div>
                </div>
              </div>

              {/* Sections as accordions */}
              <div className="space-y-6 sm:space-y-8 text-left mt-6 sm:mt-8">
                {/* 1. Authentication */}
                <AccordionItem
                  id="auth"
                  title="1. Getting Started with Authentication"
                  icon={<BookOpen size={20} className="text-[var(--color-text)] opacity-85" />}
                  delay="delay-300"
                >
                  <p className="mb-4 text-sm sm:text-base text-[var(--color-text)]/80">
                    Log in to Outflow to access your expense tracking features. Follow these steps to sign in or create an account.
                  </p>
                  <ol className="list-decimal ml-4 sm:ml-6 space-y-2 text-sm sm:text-base">
                    <li>
                      <strong>Google Sign-in</strong>:
                      <ul className="ml-4 sm:ml-6 list-disc">
                        <li>On the login screen, click the "Continue with Google" button.</li>
                        <li>Select your Google account and confirm permissions.</li>
                        <li>You'll be redirected to the Home page.</li>
                      </ul>
                    </li>
                    <li>
                      <strong>Email and Password</strong>:
                      <ul className="ml-4 sm:ml-6 list-disc">
                        <li>Click "Sign in with Email" on the login screen.</li>
                        <li>Enter your email and password, then click "Log In".</li>
                        <li>
                          To sign up, click "Sign Up", enter your email and password, and follow the verification email instructions.
                        </li>
                        <li>
                          Forgot your password? Click "Forgot Password", enter your email, and follow the reset link sent to your inbox.
                        </li>
                      </ul>
                    </li>
                  </ol>
                  <p className="mt-4 text-xs sm:text-sm text-[var(--color-text)]/70 italic">
                    Note: Ensure your email is verified for sign-up or password reset. Check your spam folder if emails don't arrive.
                  </p>
                </AccordionItem>

                {/* 2. Home Page */}
                <AccordionItem
                  id="home"
                  title="2. Navigating the Home Page"
                  icon={<HomeIcon size={20} className="text-[var(--color-text)] opacity-85" />}
                  delay="delay-500"
                >
                  <p className="mb-4 text-sm sm:text-base text-[var(--color-text)]/80">
                    The Home page is your central hub for viewing and managing expenses. Here's how to use it.
                  </p>
                  <ol className="list-decimal ml-4 sm:ml-6 space-y-2 text-sm sm:text-base">
                    <li>
                      <strong>Select a Time Period</strong>:
                      <ul className="ml-4 sm:ml-6 list-disc">
                        <li>Tap the calendar at the top to choose a month and year.</li>
                        <li>Use the arrows or dropdown to navigate to the desired period.</li>
                      </ul>
                    </li>
                    <li>
                      <strong>View Expense Summary</strong>:
                      <ul className="ml-4 sm:ml-6 list-disc">
                        <li>Check the pie chart for a visual breakdown of expenses by category.</li>
                        <li>Tap the pie chart to go to the Reports page for more details.</li>
                      </ul>
                    </li>
                    <li>
                      <strong>Manage Expenses</strong>:
                      <ul className="ml-4 sm:ml-6 list-disc">
                        <li>Scroll through the expense list, showing category icons, notes, amounts, dates, and daily totals.</li>
                        <li>Tap an expense card to edit (update note, amount, or category) or delete it.</li>
                        <li>Confirm deletions to avoid accidental removal.</li>
                      </ul>
                    </li>
                  </ol>
                </AccordionItem>

                {/* 3. Bottom Navigation Bar */}
                <AccordionItem
                  id="nav"
                  title="3. Using the Bottom Navigation Bar"
                  icon={<HomeIcon size={20} className="text-[var(--color-text)] opacity-85" />}
                  delay="delay-700"
                >
                  <p className="mb-4 text-sm sm:text-base text-[var(--color-text)]/80">
                    The bottom navigation bar is always available, letting you switch between app features quickly.
                  </p>
                  <ol className="list-decimal ml-4 sm:ml-6 space-y-2 text-sm sm:text-base">
                    <li>Tap "Home" to return to the Home page.</li>
                    <li>Tap "Reports" to view detailed expense analytics.</li>
                    <li>Tap "+ Add Expense" to record a new expense.</li>
                    <li>Tap "Search" to find specific expenses.</li>
                    <li>Tap "Profile / Settings" to manage your account and preferences.</li>
                  </ol>
                  <p className="mt-4 text-xs sm:text-sm text-[var(--color-text)]/70 italic">
                    Tip: The navigation bar is fixed at the bottom of every screen for easy access.
                  </p>
                </AccordionItem>

                {/* 4. Reports Page */}
                <AccordionItem
                  id="reports"
                  title="4. Analyzing Expenses on the Reports Page"
                  icon={<PieChart size={20} className="text-[var(--color-text)] opacity-85" />}
                  delay="delay-300"
                >
                  <p className="mb-4 text-sm sm:text-base text-[var(--color-text)]/80">
                    The Reports page helps you understand your spending patterns with charts and summaries.
                  </p>
                  <ol className="list-decimal ml-4 sm:ml-6 space-y-2 text-sm sm:text-base">
                    <li>
                      <strong>View the Pie Chart</strong>:
                      <ul className="ml-4 sm:ml-6 list-disc">
                        <li>See category-wise expense distribution for the selected period.</li>
                        <li>The top 4 categories are highlighted; others are grouped as "Others".</li>
                      </ul>
                    </li>
                    <li>
                      <strong>Apply Filters</strong>:
                      <ul className="ml-4 sm:ml-6 list-disc">
                        <li>Choose Week, Month, Quarter, or Year from the filter menu.</li>
                        <li>The chart and summary update automatically.</li>
                      </ul>
                    </li>
                    <li>
                      <strong>Check the Summary</strong>:
                      <ul className="ml-4 sm:ml-6 list-disc">
                        <li>Review Total Spend, Highest Spending Category, and Average Daily Spend.</li>
                      </ul>
                    </li>
                    <li>
                      <strong>Explore Bar Graph View</strong>:
                      <ul className="ml-4 sm:ml-6 list-disc">
                        <li>Switch to the bar graph to compare Budget vs. Actual Expense.</li>
                        <li>Stacked bars show spending patterns over time.</li>
                      </ul>
                    </li>
                    <li>
                      <strong>View Recurring Expenses</strong>:
                      <ul className="ml-4 sm:ml-6 list-disc">
                        <li>Check the list of monthly repeating expenses.</li>
                      </ul>
                    </li>
                  </ol>
                </AccordionItem>

                {/* 5. Add Expense */}
                <AccordionItem
                  id="add"
                  title="5. Adding a New Expense"
                  icon={<PlusCircle size={20} className="text-[var(--color-text)] opacity-85" />}
                  delay="delay-300"
                >
                  <p className="mb-4 text-sm sm:text-base text-[var(--color-text)]/80">
                    Record your expenses quickly using the Add Expense feature.
                  </p>
                  <ol className="list-decimal ml-4 sm:ml-6 space-y-2 text-sm sm:text-base">
                    <li>
                      Tap "+ Add Expense" in the bottom navigation bar.
                    </li>
                    <li>
                      <strong>Select a Category</strong>:
                      <ul className="ml-4 sm:ml-6 list-disc">
                        <li>Choose from 24 categories (e.g., Food, Travel, Others) in the 6x4 grid.</li>
                        <li>Tap a category to proceed.</li>
                      </ul>
                    </li>
                    <li>
                      <strong>Enter Expense Details</strong>:
                      <ul className="ml-4 sm:ml-6 list-disc">
                        <li>Use the custom numeric keyboard (0–9, backspace, ".", "+", "−") to enter the amount.</li>
                        <li>Type a note in the mandatory Note field to describe the expense (e.g., "Lunch at Cafe").</li>
                      </ul>
                    </li>
                    <li>
                      <strong>Save the Expense</strong>:
                      <ul className="ml-4 sm:ml-6 list-disc">
                        <li>Click "Submit" to save the record.</li>
                        <li>You'll return to the Home page, with the new expense in the list.</li>
                      </ul>
                    </li>
                  </ol>
                  <p className="mt-4 text-xs sm:text-sm text-[var(--color-text)]/70 italic">
                    Tip: Double-check the note for clarity, as it helps when searching later.
                  </p>
                </AccordionItem>

                {/* 6. Search Feature */}
                <AccordionItem
                  id="search"
                  title="6. Searching for Expenses"
                  icon={<SearchIcon size={20} className="text-[var(--color-text)] opacity-85" />}
                  delay="delay-300"
                >
                  <p className="mb-4 text-sm sm:text-base text-[var(--color-text)]/80">
                    Use the Search feature to find specific expenses by date, amount, category, or note.
                  </p>
                  <ol className="list-decimal ml-4 sm:ml-6 space-y-2 text-sm sm:text-base">
                    <li>Tap "Search" in the bottom navigation bar.</li>
                    <li>
                      <strong>Enter Search Terms</strong>:
                      <ul className="ml-4 sm:ml-6 list-disc">
                        <li>Type a month (e.g., "Apr") to view all April expenses.</li>
                        <li>Type a full date (e.g., "Apr 27") to view expenses from that day.</li>
                        <li>Type a year (e.g., "2025") to view all expenses from that year.</li>
                        <li>Type a year and month together (e.g., "2025 Apr" or "Apr 2025") to view all April 2025 expenses (newest to oldest).</li>
                        <li>Enter an amount (e.g., "50") to find expenses around that amount.</li>
                        <li>Search by category (e.g., "Food") or note (e.g., "Coffee").</li>
                      </ul>
                    </li>
                    <li>
                      <strong>View Results</strong>:
                      <ul className="ml-4 sm:ml-6 list-disc">
                        <li>Matching expenses appear in a list.</li>
                      </ul>
                    </li>
                    <li>
                      <strong>Clear the Search</strong>:
                      <ul className="ml-4 sm:ml-6 list-disc">
                        <li>Click the "X" button to clear the search bar and reset results.</li>
                      </ul>
                    </li>
                  </ol>
                </AccordionItem>

                {/* 7. Profile / Settings */}
                <AccordionItem
                  id="profile"
                  title="7. Managing Your Profile and Settings"
                  icon={<SettingsIcon size={20} className="text-[var(--color-text)] opacity-85" />}
                  delay="delay-300"
                >
                  <p className="mb-4 text-sm sm:text-base text-[var(--color-text)]/80">
                    Customize your account and export data in the Profile/Settings section.
                  </p>
                  <ol className="list-decimal ml-4 sm:ml-6 space-y-2 text-sm sm:text-base">
                    <li>
                      Tap "Profile / Settings" in the bottom navigation bar.
                    </li>
                    <li>
                      <strong>Update Profile</strong>:
                      <ul className="ml-4 sm:ml-6 list-disc">
                        <li>Change your name from the default "username" by editing the Name field.</li>
                      </ul>
                    </li>
                    <li>
                      <strong>Set Budget</strong>:
                      <ul className="ml-4 sm:ml-6 list-disc">
                        <li>Enter a monthly budget to track spending in the Reports page's bar graph.</li>
                        <li>Save changes to update the graph.</li>
                      </ul>
                    </li>
                    <li>
                      <strong>Export Data</strong>:
                      <ul className="ml-4 sm:ml-6 list-disc">
                        <li>Click "Export Data" to download your expenses as a PDF file.</li>
                        <li>Check your Downloads folder for the file.</li>
                      </ul>
                    </li>
                    <li>
                      <strong>Log Out</strong>:
                      <ul className="ml-4 sm:ml-6 list-disc">
                        <li>Click "Logout" to return to the Sign-In screen.</li>
                      </ul>
                    </li>
                  </ol>
                </AccordionItem>
            </div>
          </div>
        </div>
    </PageLayout>
  );
};

export default Usermanualpage;